const { createSelector } = require('reselect');
const {
  CURRENCIES,
  PREMIUM_CARD_EXCHANGE_REWARDS: { MIN, MAX },
} = require('./constants');

const {
  copyPurchaseLootpackWithImageCampaign,
  copyLootpackWithCampaignImages,
} = require('./slices/campaigns/utils');
const {
  campaignsWithImageTemplatesSelector,
} = require('./slices/campaigns/selectors');
const { isFutureAndTruthy } = require('./utils/dateAndTime');

const authSelector = ({ auth }) => auth;
const loggedSelector = createSelector(
  authSelector,
  ({ token, user: { data } }) => !!(token && data),
);
const languagePlainSelector = createSelector(authSelector, ({ language }) =>
  (language || '').slice(0, 2),
);
const userSelector = createSelector(
  authSelector,
  ({ user: { data } }) => data || { username: '' },
);
const countrySelector = createSelector(userSelector, ({ country }) => country);

// todo: create hook that refreshes subscription to prevent bugs on overpaling values when it's used in a selector
const isSubscribedSelector = createSelector(
  userSelector,
  ({ subscriptionValidUntil }) => isFutureAndTruthy(subscriptionValidUntil),
);
const dailyBonusSelector = createSelector(
  userSelector,
  ({ lastAppStartBonus }) => lastAppStartBonus || 0,
);
const favouritesSelector = createSelector(
  userSelector,
  ({ favouriteLoots = [] }) =>
    favouriteLoots.reduce((acc, value) => {
      acc[value] = true;
      return acc;
    }, {}),
);

const bannerSlotsSelector = ({ bannerSlots: { items } }) => items;
const lootsSelector = createSelector(
  ({ loots: { items } }) => items,
  campaignsWithImageTemplatesSelector,
  (loots, campaigns) => {
    const lootsWithCampaignImages = {};
    Object.keys(loots).forEach((lootId) => {
      lootsWithCampaignImages[lootId] = copyLootpackWithCampaignImages(
        campaigns,
      )(loots[lootId]);
    });
    return lootsWithCampaignImages;
  },
);
const newsSelector = ({ newsFeed: { items = [], ...rest } }) => ({
  items,
  ...rest,
});
const configurationSelector = ({ configuration: data }) => data && data.data;
const comicsSelector = ({
  comics: { items, readComics: _readComics, ...comics },
  readComics: { items: readComics },
}) => ({
  items: items.filter(
    (comic) => !comic.disabled && !isFutureAndTruthy(comic.showFrom),
  ),
  readComics,
  ...comics,
});

const purchasesSelector = createSelector(
  ({ purchases: { items } }) => items,
  (purchases) => purchases.filter(({ status }) => status === 'PAID'),
);

const unopenedPurchasesSelector = createSelector(
  ({ purchases: { items } }) => items,
  campaignsWithImageTemplatesSelector,
  (purchases, campaigns) =>
    purchases
      .filter(({ status, opened }) => status === 'PAID' && !opened)
      .map(copyPurchaseLootpackWithImageCampaign(campaigns)),
);

const lastBundleAmountSelector = ({ purchases }) =>
  purchases && purchases.lastBundleAmount;
const appStateSelector = ({ app: initialized }) => initialized;

const legalDataSelector = ({
  legal: {
    fetchingInProgressSince,
    lastSuccessfulFetch,
    data,
    consentedAt,
    consentConsumed,
    didInvalidate,
  },
}) => ({
  ...data,
  loading: !!fetchingInProgressSince || !lastSuccessfulFetch,
  consentedAt,
  consentConsumed,
  didInvalidate,
});

const legalDataAppSelectorPlain = ({
  legalApp: {
    fetchingInProgressSince,
    lastSuccessfulFetch,
    data,
    versionCode,
    didInvalidate,
  },
}) => ({
  ...data,
  versionCode,
  loading: !!fetchingInProgressSince || !lastSuccessfulFetch,
  didInvalidate,
});

const configLoadingSelectorPlain = ({
  configuration: {
    fetchingInProgressSince,
    lastSuccessfulFetch,
    latestAppVersionCode,
  },
}) => ({
  loading: !!fetchingInProgressSince || !lastSuccessfulFetch,
  latestAppVersionCode,
});

const legalDataAppSelector = createSelector(
  legalDataAppSelectorPlain,
  configLoadingSelectorPlain,
  (
    { loading, versionCode, ...data },
    { loading: configLoading, latestAppVersionCode },
  ) => ({
    ...data,
    loading: loading || configLoading || latestAppVersionCode !== versionCode,
  }),
);

const createVersionedLegalDataAppSelector = (v) => {
  if (!v) {
    return legalDataAppSelector;
  }
  return createSelector(
    legalDataAppSelectorPlain,
    ({ loading, versionCode, ...data }) => ({
      ...data,
      loading: loading || versionCode !== v,
    }),
  );
};

const showLegalDataModalSelector = createSelector(
  [
    legalDataSelector,
    userSelector,
    loggedSelector,
    (_state, params) => params?.requireAtLeastOneConsentToShow ?? false,
  ],
  (
    { _id, consentedAt, consentConsumed },
    { consents = [] },
    logged,
    requireAtLeastOneConsentToShow,
  ) =>
    !consentedAt ||
    (logged &&
      consentConsumed &&
      // We have a separate consent screen in the registration flow - we do not want to show the modal
      // if the user has not given any consents yet.
      (!requireAtLeastOneConsentToShow || !!consents.length) &&
      consents.every(({ legalData }) => _id !== legalData)),
);

const userNeedsFirstTermsConsentSelector = createSelector(
  userSelector,
  loggedSelector,
  ({ consents = [] }, logged) => logged && !consents.length,
);

const legalDataUpdatedSelector = createSelector(
  legalDataSelector,
  ({ legalDataUpdated }) => legalDataUpdated,
);

const tagsSelector = ({ tags: { items } }) => items;

const emailSelector = createSelector(
  userSelector,
  (user) => user?.email || user?.socialEmail || '',
);

const newsletterEmailSelector = createSelector(
  userSelector,
  (user) => user?.newsletterEmail || '',
);

const mappedPurchasesSelector = createSelector(
  purchasesSelector,
  (purchases) => ({
    boughtFree: purchases.some(
      ({ loot: { currency } }) => currency === CURRENCIES.FREE_CURRENCY,
    ),
    purchases: purchases.reduce((acc, { loot: { _id } }) => {
      acc[_id] = true;
      return acc;
    }, {}),
  }),
);

const shouldInviteUsersSelector = createSelector(
  loggedSelector,
  userSelector,
  (isLoggedIn, user) => isLoggedIn && user?.username,
);

// todo: make sure that all selectors depending on user return correct values when user is not logged in
const hasNoPaidOrdersSelector = createSelector(
  loggedSelector,
  userSelector,
  (isLoggedIn, { hasPaidOrder }) => isLoggedIn && !hasPaidOrder,
);

const favouriteTagSelector = createSelector(
  tagsSelector,
  (tags) => tags.find(({ tagType }) => tagType === 'favourite')?._id,
);

const tagsMappedSelector = createSelector(tagsSelector, (tags) =>
  tags.reduce((acc, value) => {
    acc[value._id] = value;
    return acc;
  }, {}),
);

const filtersSelector = ({ filters: { items } }) => items;

const createLootDetailTagSelector = (tags, separatorColorScheme) =>
  createSelector(tagsMappedSelector, (tagMap) => {
    const lootTypeTagId = tags?.find(
      ({ tag, hidden }) => !hidden && tagMap[tag]?.tagType === 'lootType',
    );

    return {
      lootTypeTag: tagMap[lootTypeTagId?.tag],
      separatorTag: tagMap[separatorColorScheme],
    };
  });

const filterableTagsSelector = createSelector(
  tagsSelector,
  loggedSelector,
  favouriteTagSelector,
  (state, platform) => platform,
  (tags, isLogged, favouriteFilter, platform = 'web') =>
    tags.filter(
      ({ filterable, platforms, _id }) =>
        filterable &&
        platforms?.[platform] &&
        (isLogged || _id !== favouriteFilter),
    ),
);

const purchasesFilteredSelector = createSelector(
  ({ purchases }) => purchases,
  campaignsWithImageTemplatesSelector,
  ({ items, ...props }, campaigns) => ({
    items: items
      .filter((purchase) => purchase.status === 'PAID' && !purchase.opened)
      .map(copyPurchaseLootpackWithImageCampaign(campaigns)),
    ...props,
  }),
);

const premiumCardExchangeRewardSelector = ({ configuration: { data } }) => ({
  minReward: data?.minPremiumTradeReward || MIN,
  maxReward: data?.maxPremiumTradeReward || MAX,
});

const landingPageSelector = createSelector(
  ({ landingPages }) => landingPages,
  (state, pageId) => pageId,
  campaignsWithImageTemplatesSelector,
  (landingPages, pageId, campaigns) => {
    const { lastSuccessfulFetch, ...landingPage } = landingPages[pageId] || {};
    const { featuredLoots, extraLoots } = landingPage;

    return {
      ...landingPage,
      featuredLoots: featuredLoots?.map(
        copyLootpackWithCampaignImages(campaigns),
      ),
      extraLoots: extraLoots?.map(copyLootpackWithCampaignImages(campaigns)),
      loading: !lastSuccessfulFetch,
    };
  },
);

const tradingCampaignBalancesSelector = ({
  tradingCampaignBalances: { items },
}) => items;
const tradingCampaignsSelector = ({ tradingCampaigns: { items } }) => items;
const campaignSelector = createSelector(
  tradingCampaignsSelector,
  (a, campaignId) => campaignId,
  (tradingCampaigns, campaignId) =>
    (tradingCampaigns[campaignId]?.lastSuccessfulFetch
      ? tradingCampaigns[campaignId]
      : Object.values(tradingCampaigns).find(
          (campaignEntry) => campaignEntry.urlAlias === campaignId,
        )) ||
    tradingCampaigns[campaignId] ||
    {},
);

const offersSelector = ({ offers: { items } }) => items || [];

const recruiterSelector = createSelector(
  userSelector,
  (user) => user.recruiter,
);

const isPasswordSetSelector = createSelector(
  userSelector,
  (user) => user.passwordHash,
);

const userNeedsEmailVerificationSelector = createSelector(
  userSelector,
  (user) => Boolean(user.email && !user.emailVerified),
);

const userMissingDetailsSelector = createSelector(
  userSelector,
  (user) => !(user.username && user.birthday && user.country),
);

const registrationNotCompletedSelector = createSelector(
  loggedSelector,
  userNeedsFirstTermsConsentSelector,
  userNeedsEmailVerificationSelector,
  userMissingDetailsSelector,
  (logged, terms, email, details) =>
    logged && [terms, email, details].some(Boolean),
);

module.exports = {
  newsSelector,
  authSelector,
  bannerSlotsSelector,
  countrySelector,
  lootsSelector,
  loggedSelector,
  languagePlainSelector,
  userSelector,
  isSubscribedSelector,
  configurationSelector,
  comicsSelector,
  purchasesSelector,
  lastBundleAmountSelector,
  appStateSelector,
  dailyBonusSelector,
  legalDataSelector,
  legalDataAppSelector,
  createVersionedLegalDataAppSelector,
  showLegalDataModalSelector,
  userNeedsFirstTermsConsentSelector,
  legalDataUpdatedSelector,
  tagsSelector,
  favouritesSelector,
  favouriteTagSelector,
  emailSelector,
  newsletterEmailSelector,
  filtersSelector,
  tagsMappedSelector,
  createLootDetailTagSelector,
  filterableTagsSelector,
  purchasesFilteredSelector,
  unopenedPurchasesSelector,
  landingPageSelector,
  premiumCardExchangeRewardSelector,
  campaignSelector,
  tradingCampaignBalancesSelector,
  offersSelector,
  recruiterSelector,
  isPasswordSetSelector,
  userNeedsEmailVerificationSelector,
  userMissingDetailsSelector,
  registrationNotCompletedSelector,
  mappedPurchasesSelector,
  hasNoPaidOrdersSelector,
  shouldInviteUsersSelector,
};
